import React, { useEffect } from "react";
import { useParams, Navigate, Link, NavLink } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BOAT_EXCURSIONS } from "../data/site";
import { useLang, loc } from "../data/i18n";
import { ExcursionPanel } from "../components/BoatDetail";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppFloat from "../components/WhatsAppFloat";
import Seo from "../components/Seo";
import { excursionSchema } from "../lib/schema";
import { Waves } from "../components/Decor";
import { BrandSymbol } from "../components/BrandLogo";

export default function ExcursionPage() {
    const { slug } = useParams();
    const { t, lang, prefix } = useLang();
    const raw = BOAT_EXCURSIONS.find((e) => e.slug === slug);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!raw) return <Navigate to={prefix || "/"} replace />;

    const excursion = loc(raw, lang);

    return (
        <div className="min-h-screen bg-cream text-ink">
            <Seo
                title={excursion.seo.title}
                description={excursion.seo.description}
                path={`${prefix}/${excursion.slug}`}
                image={excursion.image}
                jsonLd={excursionSchema(raw, lang)}
                lang={lang}
            />
            <Header />

            <main className="pt-24 md:pt-28 pb-16 md:pb-24">
                {/* Page hero */}
                <section className="relative bg-ocean text-cream py-12 md:py-16 overflow-hidden">
                    <BrandSymbol
                        size={260}
                        className="absolute -right-16 -top-10 opacity-20"
                    />
                    <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
                        <Link
                            to={prefix || "/"}
                            className="inline-flex items-center gap-2 text-lagoon hover:text-cream transition-colors text-[11px] uppercase tracking-[0.3em] font-bold mb-6"
                            data-testid="back-to-home"
                        >
                            <ArrowLeft size={14} />
                            {t.boat.backHome}
                        </Link>
                        <p className="text-[11px] uppercase tracking-[0.3em] text-lagoon font-bold mb-3 flex items-center gap-3">
                            <span className="inline-block w-8 h-px bg-lagoon" />
                            {t.boat.pageKicker}
                        </p>
                        <p className="font-serif text-4xl md:text-6xl leading-[0.98] max-w-4xl">
                            {excursion.tagline}
                        </p>
                    </div>
                    <div className="absolute -bottom-1 left-0 right-0 pointer-events-none">
                        <Waves className="w-full h-10" color="#FCF8E6" />
                    </div>
                </section>

                {/* Tabs — indexable links between excursions */}
                <nav
                    className="sticky top-16 md:top-20 z-30 bg-cream/95 backdrop-blur-sm border-b border-ocean/10"
                    aria-label={t.boat.tabsAria}
                    data-testid="excursion-tabs"
                >
                    <div className="max-w-[1400px] mx-auto px-2 md:px-10">
                        <ul className="flex overflow-x-auto no-scrollbar">
                            {BOAT_EXCURSIONS.map((e) => (
                                <li key={e.id} className="flex-shrink-0">
                                    <NavLink
                                        to={`${prefix}/${e.slug}`}
                                        end
                                        className={({ isActive }) =>
                                            `inline-flex items-center gap-2 px-5 md:px-7 py-5 text-xs md:text-sm uppercase tracking-[0.22em] font-bold transition-colors border-b-2 whitespace-nowrap ${
                                                isActive
                                                    ? "border-coral text-coral"
                                                    : "border-transparent text-ink/60 hover:text-ocean"
                                            }`
                                        }
                                        data-testid={`tab-${e.id}`}
                                    >
                                        {loc(e, lang).shortName}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                </nav>

                <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20">
                    <ExcursionPanel key={`${lang}-${excursion.slug}`} excursion={raw} isStandalone />
                </section>
            </main>

            <Footer />
            <WhatsAppFloat />
        </div>
    );
}
